import { useState, useEffect } from 'react';
import { MessageSquare, Calendar, User, Clock, Search, Filter, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

const AdminComments = ({ user, activeModule = "b2b" }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [markingReadId, setMarkingReadId] = useState(null);

  useEffect(() => {
    fetchComments();
  }, []);

  const fetchComments = async () => {
    try {
      const res = await axios.get(`${API}/admin/employee-comments`, { withCredentials: true });
      setComments(res.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredComments = comments.filter(comment => {
    const matchesSearch = !searchTerm || 
      comment.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comment.employee_comment?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDate = !filterDate || comment.date === filterDate;
    
    return matchesSearch && matchesDate;
  });

  const unreadComments = comments.filter((comment) => comment.is_unread);
  const readComments = comments.filter((comment) => !comment.is_unread);

  const markCommentAsRead = async (scheduleId) => {
    if (!scheduleId) return;
    setMarkingReadId(scheduleId);
    try {
      await axios.put(`${API}/admin/employee-comments/${scheduleId}/mark-read`, {}, { withCredentials: true });
      setComments((prev) => prev.map((comment) => (
        comment.schedule_id === scheduleId
          ? {
              ...comment,
              is_unread: false,
              employee_comment_read_at: new Date().toISOString(),
              employee_comment_read_by: user?.name || user?.email || 'Admin'
            }
          : comment
      )));
    } catch (error) {
      console.error('Failed to mark comment as read:', error);
      alert(`Erreur: ${error.response?.data?.detail || error.message}`);
    } finally {
      setMarkingReadId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return '';
    const date = new Date(dateTimeStr);
    return date.toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="space-y-6 fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0F172A] font-['Chivo']">Commentaires des employés</h1>
            <p className="text-gray-500">Consultez tous les commentaires et signalements</p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-orange-100 px-3 py-2 rounded-lg text-center" data-testid="comments-total-counter">
              <span className="text-xl font-bold text-orange-600">{comments.length}</span>
              <p className="text-xs text-orange-700">Total</p>
            </div>
            <div className="bg-red-100 px-3 py-2 rounded-lg text-center" data-testid="comments-unread-counter">
              <span className="text-xl font-bold text-red-600">{unreadComments.length}</span>
              <p className="text-xs text-red-700">Non lu</p>
            </div>
            <div className="bg-green-100 px-3 py-2 rounded-lg text-center" data-testid="comments-read-counter">
              <span className="text-xl font-bold text-green-600">{readComments.length}</span>
              <p className="text-xs text-green-700">Lu</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher par nom ou contenu..."
                  className="pl-10"
                />
              </div>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="pl-10 w-full sm:w-48"
                />
              </div>
              {(searchTerm || filterDate) && (
                <button
                  onClick={() => { setSearchTerm(''); setFilterDate(''); }}
                  className="text-sm text-blue-600 hover:underline whitespace-nowrap"
                >
                  Effacer filtres
                </button>
              )}
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F172A]"></div>
          </div>
        ) : filteredComments.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">
                {comments.length === 0 
                  ? "Aucun commentaire pour le moment"
                  : "Aucun commentaire ne correspond à vos critères"
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredComments.map((comment, index) => (
              <Card key={index} className={`hover:shadow-md transition-shadow ${comment.is_unread ? 'border-l-4 border-l-red-500 bg-red-50/40' : ''}`} data-testid={`comment-card-${comment.schedule_id || index}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                      {comment.picture ? (
                        <img src={comment.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <User className="w-6 h-6 text-orange-600" />
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-[#0F172A]">{comment.user_name}</h3>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${comment.is_unread ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`} data-testid={`comment-read-status-${comment.schedule_id || index}`}>
                            {comment.is_unread ? 'Non lu' : 'Lu'}
                          </span>
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDateTime(comment.comment_sent_at || comment.updated_at)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Comment */}
                      <div className="bg-gray-50 rounded-lg p-4 mb-3">
                        <p className="text-gray-700 whitespace-pre-wrap">{comment.employee_comment}</p>
                      </div>
                      
                      {/* Date info */}
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          Concerne le: <strong>{formatDate(comment.date)}</strong>
                        </span>
                        {comment.status && (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            comment.status === 'present' ? 'bg-green-100 text-green-700' :
                            comment.status === 'repos' ? 'bg-blue-100 text-blue-700' :
                            comment.status === 'absent' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {comment.status === 'present' ? 'Présent' :
                             comment.status === 'repos' ? 'Repos' :
                             comment.status === 'repos_force' ? 'Repos forcé' :
                             comment.status === 'absent' ? 'Absent' :
                             comment.status === 'prevu' ? 'Prévu' :
                             comment.status}
                          </span>
                        )}

                        {comment.is_unread && (
                          <button
                            onClick={() => markCommentAsRead(comment.schedule_id)}
                            disabled={markingReadId === comment.schedule_id}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-green-300 text-green-700 hover:bg-green-50 disabled:opacity-50"
                            data-testid={`comment-mark-read-${comment.schedule_id || index}`}
                          >
                            <CheckCircle2 className="w-3 h-3" />
                            {markingReadId === comment.schedule_id ? '...' : 'Marquer lu'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminComments;
